import { AutocompleteChangeReason } from '@mui/material';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { MultiSelectIdsType, SelectOption } from 'basics/options/options.types';
import { findMultipleOptionsByIds } from 'yoda-ui/utils/options.utils';
import { useYodaField } from 'yoda-ui/yodaForm';
import { YodaFieldValidation, YodaFieldValue } from 'yoda-ui/yodaForm/yodaForm.types';

const useMultiSelectYoda = (
  name: string,
  defaultValue: YodaFieldValue[] | undefined,
  onChange: Function | undefined,
  validation: YodaFieldValidation,
  options: SelectOption[],
  freeOption: boolean,
  enableSelectAll: boolean,
  onSelectedChange: ((selected: SelectOption[]) => void) | undefined,
  onDeleteOption: ((id: string) => void) | undefined,
  controlledValue: SelectOption[] | undefined,
) => {
  const {
    onChangeFieldSynthetic,
    fieldValue,
    fieldDefaultValue,
    fieldShowError,
    fieldErrorMessage,
  } = useYodaField({ name, defaultValue, onChange, validation });

  const selectAllOption = useMemo(() => ({ id: 'all', label: 'Select All' }), []);
  const isSyncingRef = useRef(false);

  const extendedOptions = useMemo(() => {
    if (!enableSelectAll) {
      return options;
    }

    return [selectAllOption, ...options].sort((a, b) => (a.id === 'all' ? -1 : b.id === 'all' ? 1 : a.label.localeCompare(b.label)));
  }, [enableSelectAll, options, selectAllOption]);

  // eslint-disable-next-line max-len
  const findCurrentMultiOptionsByIds = useCallback((idsList: MultiSelectIdsType) => findMultipleOptionsByIds(freeOption, extendedOptions, idsList), [freeOption, extendedOptions]);

  const handleChange = useCallback(
    (event: SyntheticEvent, value: (SelectOption | string)[], reason: AutocompleteChangeReason) => {
      const isSelectAllChosen = value.some((option) => (typeof option !== 'string' && option.id === 'all'));

      if (isSelectAllChosen) {
        const currentSelectedIds = Array.isArray(fieldValue) ? fieldValue : [];
        const isAllSelected = currentSelectedIds.length === options.length && options.length > 0;

        if (isAllSelected) {
          onChangeFieldSynthetic(event, [], 'clear');
        } else {
          onChangeFieldSynthetic(event, [...options], 'selectOption');
        }
        return;
      }

      onChangeFieldSynthetic(event, value, reason);
    },
    [onChangeFieldSynthetic, options, fieldValue],
  );

  const handleAutocompleteChange = useCallback(
    (event: SyntheticEvent, value: (SelectOption | string)[], reason: AutocompleteChangeReason) => {
      const isSelectAllChosen = value.some(
        (v) => typeof v !== 'string' && v.id === 'all',
      );

      let finalOptions: SelectOption[] = [];

      if (isSelectAllChosen) {
        const currentIds = Array.isArray(fieldValue) ? fieldValue : [];
        const isAllSelected = currentIds.length === options.length && options.length > 0;

        if (isAllSelected) {
          finalOptions = [];
        } else {
          finalOptions = [...options];
        }
      } else {
        finalOptions = value.filter(
          (v) => typeof v !== 'string',
        ) as SelectOption[];
      }

      handleChange(event, finalOptions, reason);

      if (!isSyncingRef.current) {
        onSelectedChange?.(finalOptions);
      }

      if (reason === 'removeOption' && !isSyncingRef.current) {
        const removed = findCurrentMultiOptionsByIds(fieldValue).find(
          (o) => !finalOptions.some((v) => v.id === o.id),
        );
        if (removed) {
          onDeleteOption?.(removed.id as string);
        }
      }
    },
    [fieldValue, options, handleChange, onSelectedChange, onDeleteOption, findCurrentMultiOptionsByIds],
  );

  useEffect(() => {
    if (controlledValue) {
      const currentIds = Array.isArray(fieldValue) ? fieldValue : [];
      const newIds = controlledValue.map((v) => v.id);

      const isDifferent = currentIds.length !== newIds.length
        || !newIds.every((id) => currentIds.includes(id));

      if (isDifferent) {
        isSyncingRef.current = true;
        handleChange(null as unknown as React.SyntheticEvent<Element, Event>, controlledValue, 'selectOption');
        setTimeout(() => {
          isSyncingRef.current = false;
        }, 0);
      }
    }
  }, [controlledValue, fieldValue, handleChange]);

  return {
    fieldValue,
    fieldDefaultValue,
    fieldShowError,
    fieldErrorMessage,
    extendedOptions,
    findCurrentMultiOptionsByIds,
    handleChange,
    handleAutocompleteChange,
    isSyncingRef,
  };
};

export default useMultiSelectYoda;
